import { useEffect } from 'react'

import { BrandMark } from '@/Ship/Components/Brand/BrandMark'
import { BrandConfig } from '@/Ship/Configs/BrandConfig'
import { classNames } from '@/Ship/Helpers/classNames'

import styles from './AuthShell.module.css'

import type { CSSProperties, ReactNode } from 'react'

export interface AuthShellProps {
    children?: ReactNode
    /** Знак і назва над заголовком. */
    showLogo?: boolean
}

/**
 * Оболонка екранів входу (`SPEC-014` §2.1).
 *
 * Спільна для ОБОХ шляхів, якими користувач бачить форму:
 *   • сторінка `/public/login` (і відновлення пароля) — через `FormWrapper`;
 *   • `templateError403` ядра, тобто `LoginPageTemplate`, — його ядро малює
 *     замість будь-якої закритої сторінки, коли сесії немає (а сюди частіше
 *     заходять із закладки, а не з `/public/login`).
 *
 * Через це вона живе в Ship: інакше один з екранів неминуче лишається
 * неоформленим.
 *
 * Компонування — як на демо-стенді ядра (рішення замовника 2026-10-14): дві
 * колонки, ліворуч знак, назва й тексти з `BrandConfig`, праворуч картка
 * входу; на вузькому екрані вони стають одна під одною.
 *
 * Тло, логотип, картинку під текстом і тексти дає активний профіль бренду
 * (ADR 048): в Астерікса це фото легасі `back.webp` під темним шаром, знак і
 * назва.
 */
export const AuthShell = ({ children, showLogo = true }: AuthShellProps) => {
    // Позначка для Ship-стилів: на екрані входу ховається кнопка відлагодження,
    // яка інакше лягає поверх картки.
    useEffect(() => {
        document.body.classList.add('wl-public-screen')

        return () => document.body.classList.remove('wl-public-screen')
    }, [])

    const { login } = BrandConfig

    return (
        <div className={styles['screen']} style={{ '--wl-auth-background': login.background } as CSSProperties}>
            <div className={styles['layout']}>
                <section className={styles['hero']}>
                    {showLogo &&
                        (login.logo ? (
                            <div className={styles['heroBrand']}>
                                <img className={styles['heroLogo']} src={login.logo} alt={BrandConfig.name} />
                            </div>
                        ) : (
                            <div className={styles['heroBrand']}>
                                <BrandMark size={52} />
                                <span className={styles['heroBrandName']}>{BrandConfig.name}</span>
                            </div>
                        ))}

                    <h1 className={styles['headline']}>
                        {login.headline.map((line) =>
                            typeof line === 'string' ? (
                                <span key={line} className={styles['headlineLine']}>
                                    {line}
                                </span>
                            ) : (
                                <span key={line.text} className={classNames(styles['headlineLine'], styles['headlineAccent'])}>
                                    {line.text}
                                </span>
                            )
                        )}
                    </h1>

                    <p className={styles['lead']}>
                        {login.lead.map((sentence) => (
                            <span key={sentence} className={styles['leadLine']}>
                                {sentence}
                            </span>
                        ))}
                    </p>

                    {login.heroImage && <img className={styles['heroImage']} src={login.heroImage} alt="" />}

                    <p className={styles['caption']}>{login.caption}</p>
                </section>

                <div className={styles['card']}>{children}</div>
            </div>
        </div>
    )
}
